import React, { useEffect, useMemo, useState } from "react"
import { Button, Card, CardBody, Col, Row, Modal, ModalHeader, ModalBody } from "reactstrap"
import Select, { components as RSComponents } from "react-select"
import Chart from "react-apexcharts"
import { FaFilter, FaUser, FaBoxOpen } from "react-icons/fa"
import { customStyles } from "utils/SelectStyles"
import { DatePicker } from "antd"
import { months } from "utils/defaultpositions"
import secureLocalStorage from "react-secure-storage"
import dayjs from "dayjs"
import { FetchPostData } from "utils/Common_api_functions"
import Loader from "components/Loader/loader"

const { RangePicker } = DatePicker

const currentYear = new Date().getFullYear()
const years = Array.from({ length: currentYear - 2020 }, (_, i) => ({
  label: `${2021 + i}`,
  value: 2021 + i,
})).reverse()

const fmtGBP = v => {
  const abs = Math.abs(v)
  return (
    (v < 0 ? "-£" : "£") +
    abs.toLocaleString("en-GB", { maximumFractionDigits: 2 })
  )
}

// ── Sort Order cycling button ────────────────────────────────────────────────
const SORT_STATES = [
  { key: "default", icon: "⇅", label: "Sort", color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" },
  { key: "ascending", icon: "↑", label: "Low → High", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  { key: "descending", icon: "↓", label: "High → Low", color: "#0f766e", bg: "#f0fdfa", border: "#99f6e4" },
]

const SortButton = ({ sortOrder, onToggle }) => {
  const current = SORT_STATES.find(s => s.key === sortOrder) || SORT_STATES[0]
  return (
    <button
      onClick={onToggle}
      title={`Sort Order: ${current.label} — click to change`}
      style={{
        display: "flex", alignItems: "center", gap: "8px",
        background: current.bg, border: `1.5px solid ${current.border}`,
        borderRadius: "10px", padding: "5px 12px",
        cursor: "pointer", outline: "none",
        transition: "all 0.18s ease", whiteSpace: "nowrap",
        userSelect: "none", minWidth: "115px",
      }}
    >
      <span style={{ fontSize: "15px", lineHeight: 1, color: current.color, fontWeight: 700, minWidth: "16px", textAlign: "center" }}>
        {current.icon}
      </span>
      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <span style={{ fontSize: "8px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", lineHeight: 1.1 }}>
          Sort Order
        </span>
        <span style={{ fontSize: "11px", fontWeight: 700, color: current.color, lineHeight: 1.3 }}>
          {current.label}
        </span>
      </span>
    </button>
  )
}

// ── Subcategory Names Map ──────────────────────────────────────────────────
const SUBCATEGORY_NAMES = {
  // Category 1 - Sausages
  "1": "Sausages",
  "1.1": "Traditional Sausages",
  "1.2": "Chipolatas",
  "1.3": "Catering Sausages",
  "1.4": "Beef Sausages",
  "1.5": "Cocktail Sausages",
  "1.6": "Flavoured Sausages",
  "1.7": "Hafners",
  "1.8": "Jumbo Sausages",
  "1.9": "Speciality Sausages",
  "1.31": "Catering Sausages",
  "1.32": "Catering Sausages",

  // Category 2 - Fresh Pork
  "2": "Fresh Pork",
  "2.1": "Fresh Pork Cuts",
  "2.2": "Fresh Pork Primal",
  "2.3": "Belly Pork",
  "2.4": "Pork Ribs",

  // Category 3 - Puddings
  "3": "Puddings",
  "3.1": "Black Pudding",
  "3.2": "White Pudding",
  "3.3": "Vegetable Pudding",

  // Category 4 - Bacon & Ham
  "4": "Bacon & Ham",
  "4.1": "Bacon",
  "4.2": "Smoked Bacon",
  "4.3": "Bacon",
  "4.4": "Gammon",
  "4.5": "Ham",
  "4.6": "Cooked Meats",

  // Category 5 - Cured
  "5": "Cured",
  "5.1": "Cured Pork",
  "5.2": "Cured Products",
}

const getSubCategoryName = r => {
  if (
    r.subCategoryName &&
    isNaN(r.subCategoryName) &&
    !/^\d+(\.\d+)?$/.test(String(r.subCategoryName).trim())
  ) {
    return r.subCategoryName
  }
  const rawCode = String(r.subCategory || r.subCategoryTwo || r.category || "").trim()
  if (SUBCATEGORY_NAMES[rawCode]) return SUBCATEGORY_NAMES[rawCode]

  const sc = String(r.subCategory || "").trim()
  if (SUBCATEGORY_NAMES[sc]) return SUBCATEGORY_NAMES[sc]

  const s2 = String(r.subCategoryTwo || "").trim()
  if (SUBCATEGORY_NAMES[s2]) return SUBCATEGORY_NAMES[s2]

  if (sc.startsWith("1.7")) return "Hafners"
  if (sc.startsWith("1.3")) return "Catering Sausages"
  if (sc.startsWith("1.2")) return "Chipolatas"
  if (sc.startsWith("1.1")) return "Traditional Sausages"
  if (sc.startsWith("4.3")) return "Bacon"
  if (sc.startsWith("4.5")) return "Ham"
  if (sc.startsWith("5.1")) return "Cured"
  if (sc.startsWith("3")) return "Puddings"
  if (sc.startsWith("2")) return "Fresh Pork"
  if (sc.startsWith("1")) return "Sausages"

  return rawCode || "Other"
}

const SELECT_ALL_VALUE = "__select_all__"

const CheckboxOption = ({
  innerProps,
  label,
  isSelected,
  isFocused,
  data,
  selectProps,
}) => {
  const isSelectAll = data?.value === SELECT_ALL_VALUE

  const allOpts = isSelectAll
    ? (selectProps?.options || []).filter(o => o.value !== SELECT_ALL_VALUE)
    : []
  const selectedCount = isSelectAll ? (selectProps?.value || []).length : 0
  const allSelected =
    isSelectAll && allOpts.length > 0 && selectedCount === allOpts.length

  const checked = isSelectAll ? allSelected : isSelected

  return (
    <div
      {...innerProps}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 10px",
        cursor: "pointer",
        background: isFocused ? "#f1f5f9" : isSelectAll ? "#f8faff" : "#fff",
        borderBottom: isSelectAll ? "1px solid #e2e8f0" : "none",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={() => { }}
        style={{
          accentColor: "#3b82f6",
          width: 14,
          height: 14,
          flexShrink: 0,
          cursor: "pointer",
        }}
      />
      <span
        style={{
          fontSize: "13px",
          color: isSelectAll ? "#1d4ed8" : "#1e293b",
          fontWeight: checked || isSelectAll ? 700 : 400,
        }}
      >
        {label}
      </span>
    </div>
  )
}

const CollapsibleValueContainer = ({ children, ...props }) => {
  const selected = props.getValue()
  const realOpts = (props.selectProps?.options || []).filter(
    o => o.value !== SELECT_ALL_VALUE
  )
  const allSelected = realOpts.length > 0 && selected.length === realOpts.length
  if (allSelected) {
    const input = React.Children.toArray(children).slice(-1)
    return (
      <RSComponents.ValueContainer {...props}>
        <span
          style={{
            color: "#1d4ed8",
            fontWeight: 600,
            fontSize: "13px",
            padding: "2px 0",
          }}
        >
          All ({realOpts.length}) selected
        </span>
        {input}
      </RSComponents.ValueContainer>
    )
  }
  return (
    <RSComponents.ValueContainer {...props}>
      {children}
    </RSComponents.ValueContainer>

  )
}

const NetMarginPage = () => {
  const [loading, setLoading] = useState(false)
  const [netMarginData, setNetMarginData] = useState({})
  const [selectedCustomers, setSelectedCustomers] = useState([])
  const [customerSort, setCustomerSort] = useState("asc")
  const [customerMenuOpen, setCustomerMenuOpen] = useState(false)
  const [selectedProducts, setSelectedProducts] = useState([])
  const [productSort, setProductSort] = useState("asc")
  const [productMenuOpen, setProductMenuOpen] = useState(false)
  const [dateRange, setDateRange] = useState([])
  const [selectedMonths, setSelectedMonths] = useState([])
  const [selectedYears, setSelectedYears] = useState([])
  const [currentWeek, setCurrentWeek] = useState([])
  const [maxDate, setMaxDate] = useState(null)
  const [barSortOrder, setBarSortOrder] = useState("default")

  // ── Drill-down modal state ──────────────────────────────────────────────────
  const [drillModal, setDrillModal] = useState(false)
  const [selectedProductDrill, setSelectedProductDrill] = useState(null)
  const [modalSortKey, setModalSortKey] = useState("default")
  const [modalSortOrder, setModalSortOrder] = useState("desc")

  useEffect(() => {
    const Months = secureLocalStorage?.getItem("Months")
    const DateRange = secureLocalStorage?.getItem("DateRange")
    const FinalRangeRaw = secureLocalStorage?.getItem("FINALRANGE")
    const Years = secureLocalStorage?.getItem("Years")

    setSelectedMonths(Months || [])
    setSelectedYears(Years || [])

    const activeDateRaw = DateRange ?? FinalRangeRaw
    const activeDateRange = activeDateRaw
      ? activeDateRaw.map(d => dayjs(d))
      : []
    setDateRange(activeDateRange)
    setCurrentWeek(activeDateRange)
    setMaxDate(FinalRangeRaw?.[1] ? dayjs(FinalRangeRaw[1]) : null)

    const fetchInitialData = async () => {
      const payload = {
        startDate: activeDateRange?.[0]?.isValid()
          ? activeDateRange[0].format("YYYY-MM-DD")
          : undefined,
        endDate: activeDateRange?.[1]?.isValid()
          ? activeDateRange[1].format("YYYY-MM-DD")
          : undefined,
        month: Months?.map(i => i?.value)?.join(",") || undefined,
        year: Years?.map(i => i?.value)?.join(",") || undefined,
      }
      const res = await FetchPostData(
        setLoading,
        "sheet/customer/report",
        payload
      )
      if (res?.data?.success) setNetMarginData(res.data?.data)
      else setNetMarginData({})
    }

    fetchInitialData()
  }, [])

  const handleFilter = async () => {
    setSelectedCustomers([])
    setSelectedProducts([])
    const res = await FetchPostData(setLoading, "sheet/customer/report", {
      startDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      endDate: dateRange?.[1]?.format("YYYY-MM-DD"),
      month: selectedMonths?.map(i => i?.value)?.join(","),
      year: selectedYears?.map(i => i?.value)?.join(","),
    })
    if (res?.data?.success) setNetMarginData(res.data?.data)
    else setNetMarginData({})
  }

  // ── Derived from API data ───────────────────────────────────────────────────

  const rawRecords = useMemo(() => {
    if (!netMarginData || typeof netMarginData !== "object") return []
    return Array.isArray(netMarginData)
      ? netMarginData
      : Object.values(netMarginData)
  }, [netMarginData])

  const customerOptions = useMemo(() => {
    const seen = new Set()
    const opts = []
    rawRecords.forEach(r => {
      if (!seen.has(r.customerCode)) {
        seen.add(r.customerCode)
        opts.push({ label: r.customerName, value: r.customerCode })
      }
    })
    return opts
  }, [rawRecords])

  const sortedCustomerOptions = useMemo(
    () =>
      [...customerOptions].sort((a, b) =>
        customerSort === "asc"
          ? a.label.localeCompare(b.label)
          : b.label.localeCompare(a.label)
      ),
    [customerOptions, customerSort]
  )

  const customerOptionsWithAll = useMemo(
    () =>
      sortedCustomerOptions.length > 0
        ? [
          { label: "Select All", value: SELECT_ALL_VALUE },
          ...sortedCustomerOptions,
        ]
        : sortedCustomerOptions,
    [sortedCustomerOptions]
  )

  const handleCustomerChange = selected => {
    if (!selected) return setSelectedCustomers([])
    if (selected.some(o => o.value === SELECT_ALL_VALUE)) {
      const allPicked =
        selectedCustomers.length === sortedCustomerOptions.length
      setSelectedCustomers(allPicked ? [] : sortedCustomerOptions)
      setCustomerMenuOpen(false)
    } else {
      setSelectedCustomers(selected)
    }
  }

  const productOptions = useMemo(() => {
    const seen = new Set()
    const opts = []
    rawRecords.forEach(r => {
      if (!seen.has(r.productCode)) {
        seen.add(r.productCode)
        opts.push({ label: r.productName, value: r.productCode })
      }
    })
    return opts
  }, [rawRecords])

  const sortedProductOptions = useMemo(
    () =>
      [...productOptions].sort((a, b) =>
        productSort === "asc"
          ? a.label.localeCompare(b.label)
          : b.label.localeCompare(a.label)
      ),
    [productOptions, productSort]
  )

  const productOptionsWithAll = useMemo(
    () =>
      sortedProductOptions.length > 0
        ? [
          { label: "Select All", value: SELECT_ALL_VALUE },
          ...sortedProductOptions,
        ]
        : sortedProductOptions,
    [sortedProductOptions]
  )

  const handleProductChange = selected => {
    if (!selected) return setSelectedProducts([])
    if (selected.some(o => o.value === SELECT_ALL_VALUE)) {
      const allPicked = selectedProducts.length === sortedProductOptions.length
      setSelectedProducts(allPicked ? [] : sortedProductOptions)
      setProductMenuOpen(false)
    } else {
      setSelectedProducts(selected)
    }
  }

  // customerCode → [{ name, code, value }]
  const customerProductMap = useMemo(() => {
    const map = {}
    rawRecords.forEach(r => {
      const cc = r.customerCode
      if (!map[cc]) map[cc] = {}
      if (!map[cc][r.productCode])
        map[cc][r.productCode] = {
          name: r.productName,
          code: r.productCode,
          value: 0,
        }
      map[cc][r.productCode].value += Number(r.netMarginContribution)
    })
    const result = {}
    Object.entries(map).forEach(([cc, prods]) => {
      result[cc] = Object.values(prods)
    })
    return result
  }, [rawRecords])

  // customerCode → total net margin contribution
  const customerTotals = useMemo(() => {
    const map = {}
    rawRecords.forEach(r => {
      map[r.customerCode] =
        (map[r.customerCode] || 0) + Number(r.netMarginContribution)
    })
    return map
  }, [rawRecords])

  // customerCode → customerName
  const customerNames = useMemo(() => {
    const map = {}
    rawRecords.forEach(r => {
      map[r.customerCode] = r.customerName
    })
    return map
  }, [rawRecords])

  // productCode → [customerCode]
  const productCustomerMap = useMemo(() => {
    const map = {}
    rawRecords.forEach(r => {
      if (!map[r.productCode]) map[r.productCode] = new Set()
      map[r.productCode].add(r.customerCode)
    })
    const result = {}
    Object.entries(map).forEach(([pc, set]) => {
      result[pc] = Array.from(set)
    })
    return result
  }, [rawRecords])

  // All products aggregated (for no-filter state)
  const allProductsAgg = useMemo(() => {
    const map = {}
    rawRecords.forEach(r => {
      if (!map[r.productCode])
        map[r.productCode] = { name: r.productName, code: r.productCode, value: 0 }
      map[r.productCode].value += Number(r.netMarginContribution)
    })
    return Object.values(map).sort((a, b) => b.value - a.value)
  }, [rawRecords])

  // ── Filter logic ───────────────────────────────────────────────────────────

  const customerScope = useMemo(() => {
    const hasC = selectedCustomers.length > 0
    const hasP = selectedProducts.length > 0

    if (!hasC && !hasP) return Object.keys(customerProductMap)

    if (hasC && hasP) {
      const selectedProductCodes = new Set(selectedProducts.map(p => p.value))
      return selectedCustomers
        .map(c => c.value)
        .filter(cc =>
          (customerProductMap[cc] || []).some(item =>
            selectedProductCodes.has(item.code)
          )
        )
    }

    if (hasC) return selectedCustomers.map(c => c.value)

    const set = new Set()
    selectedProducts.forEach(p =>
      (productCustomerMap[p.value] || []).forEach(cc => set.add(cc))
    )
    return Array.from(set)
  }, [
    selectedCustomers,
    selectedProducts,
    customerProductMap,
    productCustomerMap,
  ])

  const products = useMemo(() => {
    const hasC = selectedCustomers.length > 0
    const hasP = selectedProducts.length > 0

    if (!hasC && !hasP) return allProductsAgg

    const selectedProductCodes = hasP
      ? new Set(selectedProducts.map(p => p.value))
      : null

    const map = {}
    customerScope.forEach(cc => {
      ; (customerProductMap[cc] || []).forEach(item => {
        if (selectedProductCodes && !selectedProductCodes.has(item.code)) return
        if (!map[item.code]) map[item.code] = { name: item.name, code: item.code, value: 0 }
        map[item.code].value += item.value
      })
    })
    return Object.values(map).sort((a, b) => b.value - a.value)
  }, [
    customerScope,
    selectedCustomers,
    selectedProducts,
    allProductsAgg,
    customerProductMap,
  ])

  const chipCustomers = useMemo(() => {
    const hasC = selectedCustomers.length > 0
    const hasP = selectedProducts.length > 0
    if (!hasC && !hasP) return []

    const selectedProductCodes = hasP
      ? new Set(selectedProducts.map(p => p.value))
      : null

    return customerScope.map(cc => {
      const val = selectedProductCodes
        ? (customerProductMap[cc] || [])
          .filter(item => selectedProductCodes.has(item.code))
          .reduce((s, item) => s + item.value, 0)
        : customerTotals[cc] || 0
      return { label: customerNames[cc] || cc, value: val }
    })
  }, [
    customerScope,
    selectedCustomers,
    selectedProducts,
    customerProductMap,
    customerTotals,
    customerNames,
  ])

  console.log(chipCustomers)

  // ── Summary stats ──────────────────────────────────────────────────────────

  const positiveSum = products
    .filter(i => i.value > 0)
    .reduce((s, i) => s + i.value, 0)
  const negativeSum = products
    .filter(i => i.value < 0)
    .reduce((s, i) => s + i.value, 0)
  const net = positiveSum + negativeSum
  const topProduct = products[0] || null

  // ── Bar chart ──────────────────────────────────────────────────────────────

  const handleOpenDrilldown = prod => {
    setSelectedProductDrill(prod)
    setModalSortKey("default")
    setModalSortOrder("desc")
    setDrillModal(true)
  }

  const handleBarClick = (_event, _ctx, config) => {
    const idx = config?.dataPointIndex
    if (idx < 0 || idx === undefined) return
    const prod = barProducts[idx]
    if (!prod) return
    handleOpenDrilldown(prod)
  }

  const handleBarSortToggle = () => {
    setBarSortOrder(prev =>
      prev === "default"
        ? "ascending"
        : prev === "ascending"
          ? "descending"
          : "default"
    )
  }

  useEffect(() => {
    setBarSortOrder("default")
  }, [netMarginData])

  const barProducts = useMemo(() => {
    const top5 = products.filter(i => i.value > 0).slice(0, 5)
    const bottom5 = products
      .filter(i => i.value < 0)
      .sort((a, b) => a.value - b.value)
      .slice(0, 5)
    const combined = [...top5, ...bottom5]
    if (barSortOrder === "ascending") {
      return [...combined].sort((a, b) => a.value - b.value)
    }
    if (barSortOrder === "descending") {
      return [...combined].sort((a, b) => b.value - a.value)
    }
    return combined
  }, [products, barSortOrder])

  // Drilldown modal data filtered ONLY for the clicked product
  const drillModalData = useMemo(() => {
    if (!selectedProductDrill || !rawRecords.length) return []
    const targetCode = selectedProductDrill.code
    const targetName = selectedProductDrill.name
    const activeCustomerScope = customerScope && customerScope.length > 0 ? new Set(customerScope) : null

    const map = {}
    rawRecords.forEach(r => {
      const matchProd = (targetCode && r.productCode === targetCode) || (targetName && r.productName === targetName)
      if (!matchProd) return
      if (activeCustomerScope && !activeCustomerScope.has(r.customerCode)) return

      const key = r.customerCode || r.customerName
      if (!map[key]) {
        map[key] = {
          customerName: r.customerName || r.customerCode || "Unknown",
          customerCode: r.customerCode,
          productName: r.productName,
          salesValue: 0,
          netMarginContribution: 0,
          netMarginPercent: Number(r.netMarginPercent || 0),
          count: 0,
        }
      }
      map[key].salesValue += Number(r.weeklySalesValue || r.salesValue || 0)
      map[key].netMarginContribution += Number(r.netMarginContribution || 0)
      map[key].count += 1
      if (r.netMarginPercent !== undefined && r.netMarginPercent !== null && r.netMarginPercent !== "") {
        map[key].netMarginPercent = Number(r.netMarginPercent)
      }
    })

    return Object.values(map).map(item => {
      const pct = item.salesValue !== 0
        ? (item.netMarginContribution / item.salesValue) * 100
        : item.netMarginPercent
      return {
        ...item,
        netMarginPercent: pct,
      }
    })
  }, [selectedProductDrill, rawRecords, customerScope])

  const handleModalHeaderSort = key => {
    if (modalSortKey === key) {
      setModalSortOrder(prev => (prev === "asc" ? "desc" : "asc"))
    } else {
      setModalSortKey(key)
      setModalSortOrder(key === "customerName" ? "asc" : "desc")
    }
  }

  const handleModalSortToggle = () => {
    if (modalSortKey === "default") {
      setModalSortKey("netMarginContribution")
      setModalSortOrder("asc")
    } else if (modalSortKey === "netMarginContribution" && modalSortOrder === "asc") {
      setModalSortKey("netMarginContribution")
      setModalSortOrder("desc")
    } else {
      setModalSortKey("default")
      setModalSortOrder("desc")
    }
  }

  const modalSortStateKey = useMemo(() => {
    if (modalSortKey === "default") return "default"
    if (modalSortKey === "netMarginContribution" && modalSortOrder === "asc")
      return "ascending"
    if (modalSortKey === "netMarginContribution" && modalSortOrder === "desc")
      return "descending"
    return "default"
  }, [modalSortKey, modalSortOrder])

  const sortedModalData = useMemo(() => {
    if (!drillModalData || !Array.isArray(drillModalData)) return []
    if (!modalSortKey || modalSortKey === "default") return drillModalData

    return [...drillModalData].sort((a, b) => {
      let valA = a[modalSortKey]
      let valB = b[modalSortKey]

      if (
        modalSortKey === "netMarginPercent" ||
        modalSortKey === "netMarginContribution"
      ) {
        valA = Number(valA) || 0
        valB = Number(valB) || 0
        return modalSortOrder === "asc" ? valA - valB : valB - valA
      } else {
        valA = String(valA || "").toLowerCase()
        valB = String(valB || "").toLowerCase()
        return modalSortOrder === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA)
      }
    })
  }, [drillModalData, modalSortKey, modalSortOrder])

  const barOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        background: "transparent",
        events: {
          dataPointSelection: handleBarClick,
        },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: "65%",
          distributed: true,
        },
      },
      colors: barProducts.map(i => (i.value >= 0 ? "#22C55E" : "#EF5350")),
      dataLabels: {
        enabled: true,
        textAnchor: "middle",
        formatter: val => fmtGBP(val),
        style: { fontSize: "11px", fontWeight: 700, colors: ["#1a1a2e"] },
        offsetX: 0,
        background: {
          enabled: true,
          foreColor: "#fff",
          padding: 4,
          borderRadius: 4,
          borderWidth: 0,
          opacity: 0.85,
          dropShadow: { enabled: false },
        },
      },
      xaxis: {
        categories: barProducts.map(i => i.name),
        labels: {
          formatter: val => fmtGBP(Number(val)),
          style: { fontSize: "11px", colors: "#64748b" },
        },
      },
      yaxis: {
        labels: {
          style: { fontSize: "11px", fontWeight: 600, colors: "#1e293b" },
        },
      },
      annotations: {
        xaxis: [
          {
            x: 0,
            strokeDashArray: 4,
            borderColor: "#333",
            borderWidth: 2,
            label: {
              borderColor: "#333",
              style: {
                color: "#333",
                background: "#fff",
                fontSize: "10px",
                fontWeight: 700,
              },
              text: "0",
              position: "top",
            },
          },
        ],
      },
      grid: {
        borderColor: "#f1f5f9",
        strokeDashArray: 4,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
      },
      tooltip: {
        custom: ({ dataPointIndex }) => {
          const prod = barProducts[dataPointIndex]
          if (!prod) return ""
          const color = prod.value >= 0 ? "#22C55E" : "#EF5350"
          return `<div style="padding:10px 14px;background:#1e293b;border-radius:8px;min-width:220px;color:#f1f5f9;font-size:12px">
            <div style="font-size:11px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px">Product</div>
            <div style="font-weight:600;margin-bottom:4px">${prod.name}</div>
            <div style="font-weight:800;color:${color};font-size:14px">${fmtGBP(prod.value)}</div>
            <div style="font-size:10px;color:#94a3b8;margin-top:4px">Click bar to view customer &amp; subcategory breakdown</div>
          </div>`
        },
      },
      legend: { show: false },
    }),
    [barProducts]
  )

  const barSeries = useMemo(
    () => [
      { name: "Net Margin", data: barProducts.map(i => Math.round(i.value)) },
    ],
    [barProducts]
  )

  // ── Donut chart ────────────────────────────────────────────────────────────

  const donutItems = products.filter(i => i.value !== 0)

  const DONUT_COLORS = [
    "#4361ee",
    "#22C55E",
    "#F97316",
    "#8B5CF6",
    "#EC4899",
    "#14B8A6",
    "#EAB308",
    "#EF5350",
    "#0EA5E9",
    "#A855F7",
    "#64748b",
    "#10B981",
    "#F43F5E",
    "#6366F1",
    "#D97706",
  ]

  const donutOptions = useMemo(
    () => ({
      chart: { type: "donut", toolbar: { show: false } },
      labels: donutItems.map(i => i.name),
      colors: DONUT_COLORS,
      dataLabels: { enabled: false },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              total: {
                show: true,
                label: "Net Total",
                formatter: () => fmtGBP(net),
                fontSize: "13px",
                fontWeight: 700,
                color: net >= 0 ? "#15803d" : "#be123c",
              },
            },
          },
        },
      },
      legend: { show: false },
      tooltip: { y: { formatter: val => fmtGBP(val) } },
    }),
    [donutItems, net]
  )

  const donutSeries = useMemo(
    () => donutItems.map(i => Math.abs(i.value)),
    [donutItems]
  )

  const barHeight = Math.max(280, barProducts.length * 40)

  return (
    <div className="page-content">
      {loading && <Loader />}
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #f8faff 100%)",
          borderRadius: "16px",
          border: "1px solid #ede9fe",
          boxShadow: "0 4px 24px rgba(108,99,255,0.07)",
          padding: "16px 20px",
          marginBottom: "20px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
          <div className="d-flex gap-3">
            <div>
              <h4
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#000",
                }}
              >
                Customer Net Margin Report
              </h4>
              <p style={{ margin: 0, fontSize: "12px", color: "#666" }}>
                Product-level net margin contribution by customer
              </p>
            </div>
            <div
              style={{ width: "1px", height: "36px", background: "#e2e8f0" }}
            />
            {currentWeek?.[0] && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "10px",
                  padding: "6px 12px",
                }}
              >
                <span
                  style={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: "#16a34a",
                    display: "inline-block",
                    boxShadow: "0 0 0 2px #bbf7d0",
                  }}
                />
                <div>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#15803d",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                    }}
                  >
                    Latest week Upload
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#166534",
                      fontWeight: 600,
                      marginTop: "1px",
                    }}
                  >
                    {currentWeek[0].format("DD MMM")} —{" "}
                    {currentWeek[1].format("DD MMM YYYY")}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <RangePicker
              value={dateRange}
              placeholder={["Start (Sat)", "End (Fri)"]}
              onChange={vals => setDateRange(vals || [])}
              format="DD-MM-YYYY"
              style={{ borderRadius: "8px", height: "38px" }}
              disabledDate={current =>
                maxDate ? current && current > maxDate.endOf("day") : false
              }
            />
            <Select
              placeholder="Month"
              options={months}
              styles={customStyles}
              isMulti
              value={selectedMonths}
              onChange={v => setSelectedMonths(v || [])}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <Select
              placeholder="Year"
              options={years}
              styles={customStyles}
              isMulti
              value={selectedYears}
              onChange={v => setSelectedYears(v || [])}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <Button
              onClick={handleFilter}
              className="btn-blue rounded-2 border-transparent"
            >
              <FaFilter size={12} />
            </Button>
          </div>
        </div>
      </div>

      {/* ── Customer + Product Filters ─────────────────────────────────────── */}
      <Card
        className="mb-4 shadow-sm"
        style={{ borderRadius: "16px", border: "1px solid #dbeafe" }}
      >
        <CardBody style={{ background: "#f8faff", borderRadius: "16px" }}>
          <Row className="g-3 mb-3">
            <Col md={6}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <FaUser size={12} color="#3b82f6" />
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#1e40af",
                  }}
                >
                  Customer(s)
                </span>
                <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                  → filters products
                </span>
                <button
                  onClick={() =>
                    setCustomerSort(s => (s === "asc" ? "desc" : "asc"))
                  }
                  style={{
                    marginLeft: "auto",
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    borderRadius: "6px",
                    padding: "2px 9px",
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#1d4ed8",
                    cursor: "pointer",
                    letterSpacing: "0.3px",
                  }}
                >
                  {customerSort === "asc" ? "A → Z" : "Z → A"}
                </button>
              </div>
              <Select
                isMulti
                options={customerOptionsWithAll}
                value={selectedCustomers}
                onChange={handleCustomerChange}
                styles={customStyles}
                placeholder="Select customer(s)…"
                closeMenuOnSelect={false}
                menuIsOpen={customerMenuOpen}
                onMenuOpen={() => setCustomerMenuOpen(true)}
                onMenuClose={() => setCustomerMenuOpen(false)}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                components={{
                  Option: CheckboxOption,
                  ValueContainer: CollapsibleValueContainer,
                }}
                hideSelectedOptions={false}
              />
            </Col>

            <Col md={6}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <FaBoxOpen size={12} color="#8b5cf6" />
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#6d28d9",
                  }}
                >
                  Product(s)
                </span>
                <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                  → filters customers
                </span>
                <button
                  onClick={() =>
                    setProductSort(s => (s === "asc" ? "desc" : "asc"))
                  }
                  style={{
                    marginLeft: "auto",
                    background: "#faf5ff",
                    border: "1px solid #e9d5ff",
                    borderRadius: "6px",
                    padding: "2px 9px",
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#7c3aed",
                    cursor: "pointer",
                    letterSpacing: "0.3px",
                  }}
                >
                  {productSort === "asc" ? "A → Z" : "Z → A"}
                </button>
              </div>
              <Select
                isMulti
                options={productOptionsWithAll}
                value={selectedProducts}
                onChange={handleProductChange}
                styles={customStyles}
                placeholder="Select product(s)…"
                closeMenuOnSelect={false}
                menuIsOpen={productMenuOpen}
                onMenuOpen={() => setProductMenuOpen(true)}
                onMenuClose={() => setProductMenuOpen(false)}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                components={{
                  Option: CheckboxOption,
                  ValueContainer: CollapsibleValueContainer,
                }}
                hideSelectedOptions={false}
              />
            </Col>
          </Row>

          {chipCustomers.length > 0 &&
            chipCustomers.length < customerOptions.length && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginTop: "4px",
                }}
              >
                {chipCustomers.map((c, i) => (
                  <div
                    key={i}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      background: "#fff",
                      border: `1px solid ${c.value >= 0 ? "#bbf7d0" : "#fecdd3"
                        }`,
                      borderRadius: "20px",
                      padding: "4px 14px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#1e293b",
                      }}
                    >
                      {c.label}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: c.value >= 0 ? "#15803d" : "#be123c",
                      }}
                    >
                      {fmtGBP(c.value)}
                    </span>
                  </div>
                ))}
                {(() => {
                  const total = chipCustomers.reduce((s, c) => s + c.value, 0)
                  return (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        background: total >= 0 ? "#f0fdf4" : "#fff1f2",
                        border: `1px solid ${total >= 0 ? "#86efac" : "#fca5a5"
                          }`,
                        borderRadius: "20px",
                        padding: "4px 14px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#64748b",
                        }}
                      >
                        Total
                      </span>
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 800,
                          color: total >= 0 ? "#15803d" : "#be123c",
                        }}
                      >
                        {fmtGBP(total)}
                      </span>
                    </div>
                  )
                })()}
              </div>
            )}
        </CardBody>
      </Card>

      {/* ── Stats Cards ────────────────────────────────────────────────────── */}
      <Row className="mb-4 g-3">
        {[
          {
            label: "Positive Products (Revenue)",
            value: positiveSum,
            color: "#15803d",
            bg: "#f0fdf4",
            border: "#bbf7d0",
          },
          {
            label: "Negative Products (Revenue)",
            value: negativeSum,
            color: "#be123c",
            bg: "#fff1f2",
            border: "#fecdd3",
          },
          {
            label: "Net Margin",
            value: net,
            color: net >= 0 ? "#15803d" : "#be123c",
            bg: net >= 0 ? "#f0fdf4" : "#fff1f2",
            border: net >= 0 ? "#bbf7d0" : "#fecdd3",
          },
          {
            label: "Top Product",
            value: topProduct?.value ?? 0,
            sublabel: topProduct?.name || "—",
            color: "#1e40af",
            bg: "#eff6ff",
            border: "#bfdbfe",
          },
        ].map((card, i) => (
          <Col xl={3} md={6} key={i}>
            <div
              style={{
                background: card.bg,
                border: `1px solid ${card.border}`,
                borderRadius: "16px",
                padding: "16px 20px",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#444",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {card.label}{" "}
                {card?.sublabel && (
                  <span className="text-black fw-bold">({card.sublabel})</span>
                )}
              </p>
              <p
                style={{
                  margin: "6px 0 0",
                  fontSize: "22px",
                  fontWeight: 800,
                  color: card.color,
                }}
              >
                {fmtGBP(card.value)}
              </p>
            </div>
          </Col>
        ))}
      </Row>

      {/* ── Charts ─────────────────────────────────────────────────────────── */}
      <Row className="g-3 mb-4">
        <Col xl={6}>
          <Card className="shadow-sm h-100" style={{ borderRadius: "16px" }}>
            <CardBody>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "12px",
                }}
              >
                <div>
                  <h5
                    style={{
                      margin: 0,
                      fontWeight: 700,
                      color: "#1a1a2e",
                      fontSize: "15px",
                    }}
                  >
                    Net Margin by Product
                  </h5>
                  <p
                    style={{
                      margin: "2px 0 0",
                      fontSize: "12px",
                      color: "#64748b",
                    }}
                  >
                    Top 5 positive · Top 5 negative
                  </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <SortButton
                    sortOrder={barSortOrder}
                    onToggle={handleBarSortToggle}
                  />
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#15803d",
                    }}
                  >
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        background: "#22C55E",
                        display: "inline-block",
                      }}
                    />
                    Positive
                  </span>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#be123c",
                    }}
                  >
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        background: "#EF5350",
                        display: "inline-block",
                      }}
                    />
                    Negative
                  </span>
                </div>
              </div>
              {products.length > 0 ? (
                <Chart
                  options={barOptions}
                  series={barSeries}
                  type="bar"
                  height={barHeight}
                />
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 200,
                    color: "#94a3b8",
                    fontSize: "14px",
                  }}
                >
                  No data
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col xl={6}>
          <Card className="shadow-sm h-100" style={{ borderRadius: "16px" }}>
            <CardBody>
              <h5
                style={{
                  margin: 0,
                  fontWeight: 700,
                  color: "#1a1a2e",
                  fontSize: "15px",
                }}
              >
                Product Contribution Share
              </h5>
              <p
                style={{
                  margin: "2px 0 12px",
                  fontSize: "12px",
                  color: "#64748b",
                }}
              >
                Distribution by absolute value
              </p>
              {donutItems.length > 0 ? (
                <>
                  <Chart
                    options={donutOptions}
                    series={donutSeries}
                    type="donut"
                    height={250}
                  />
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: "6px 12px",
                      marginTop: "12px",
                      maxHeight: "180px",
                      overflowY: "auto",
                      paddingRight: "4px",
                    }}
                  >
                    {donutItems.map((item, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "3px",
                            background: DONUT_COLORS[i % DONUT_COLORS.length],
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#374151",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                          }}
                          title={item.name}
                        >
                          {item.name}
                        </span>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 700,
                            color: item.value >= 0 ? "#15803d" : "#be123c",
                            flexShrink: 0,
                          }}
                        >
                          {fmtGBP(item.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 200,
                    color: "#94a3b8",
                    fontSize: "14px",
                  }}
                >
                  No data
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ── Top / Bottom tables ────────────────────────────────────────────── */}
      <Row className="g-3">
        <Col xl={6}>
          <Card className="shadow-sm" style={{ borderRadius: "16px" }}>
            <CardBody>
              <h5
                style={{
                  margin: "0 0 12px",
                  fontWeight: 700,
                  color: "#1a1a2e",
                  fontSize: "15px",
                }}
              >
                Top Positive Products
              </h5>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #f1f5f9" }}>
                    {[
                      { label: "Product", align: "left" },
                      { label: "Net Margin", align: "right" },
                      { label: "Share", align: "right" },
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          textAlign: h.align,
                          padding: "6px 8px",
                          fontSize: "12px",
                          color: "#64748b",
                          fontWeight: 700,
                        }}
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products
                    .filter(i => i.value > 0)
                    .slice(0, 8)
                    .map((item, idx) => {
                      const share = positiveSum
                        ? ((item.value / positiveSum) * 100).toFixed(1)
                        : "0.0"
                      return (
                        <tr
                          key={idx}
                          onClick={() => handleOpenDrilldown(item)}
                          style={{ borderBottom: "1px solid #f8fafc", cursor: "pointer" }}
                          title="Click to view customer & subcategory breakdown"
                        >
                          <td
                            style={{
                              padding: "8px",
                              fontSize: "13px",
                              color: "#1e293b",
                              fontWeight: 500,
                            }}
                          >
                            {item.name}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              textAlign: "right",
                              fontSize: "13px",
                              fontWeight: 700,
                              color: "#15803d",
                            }}
                          >
                            {fmtGBP(item.value)}
                          </td>
                          <td style={{ padding: "8px", textAlign: "right" }}>
                            <span
                              style={{
                                background: "#f0fdf4",
                                border: "1px solid #bbf7d0",
                                borderRadius: "6px",
                                padding: "2px 8px",
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#15803d",
                              }}
                            >
                              {share}%
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                </tbody>
              </table>
            </CardBody>
          </Card>
        </Col>

        <Col xl={6}>
          <Card className="shadow-sm" style={{ borderRadius: "16px" }}>
            <CardBody>
              <h5
                style={{
                  margin: "0 0 12px",
                  fontWeight: 700,
                  color: "#1a1a2e",
                  fontSize: "15px",
                }}
              >
                Top Negative Products
              </h5>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #f1f5f9" }}>
                    {[
                      { label: "Product", align: "left" },
                      { label: "Net Margin", align: "right" },
                      { label: "Share of Loss", align: "right" },
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          textAlign: h.align,
                          padding: "6px 8px",
                          fontSize: "12px",
                          color: "#64748b",
                          fontWeight: 700,
                        }}
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products
                    .filter(i => i.value < 0)
                    .sort((a, b) => a.value - b.value)
                    .slice(0, 8)
                    .map((item, idx) => {
                      const share = negativeSum
                        ? ((item.value / negativeSum) * 100).toFixed(1)
                        : "0.0"
                      return (
                        <tr
                          key={idx}
                          onClick={() => handleOpenDrilldown(item)}
                          style={{ borderBottom: "1px solid #f8fafc", cursor: "pointer" }}
                          title="Click to view customer & subcategory breakdown"
                        >
                          <td
                            style={{
                              padding: "8px",
                              fontSize: "13px",
                              color: "#1e293b",
                              fontWeight: 500,
                            }}
                          >
                            {item.name}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              textAlign: "right",
                              fontSize: "13px",
                              fontWeight: 700,
                              color: "#be123c",
                            }}
                          >
                            {fmtGBP(item.value)}
                          </td>
                          <td style={{ padding: "8px", textAlign: "right" }}>
                            <span
                              style={{
                                background: "#fff1f2",
                                border: "1px solid #fecdd3",
                                borderRadius: "6px",
                                padding: "2px 8px",
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#be123c",
                              }}
                            >
                              {share}%
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                </tbody>
              </table>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ── Customer & Subcategory Drill-Down Modal ────────────────────────────── */}
      <Modal
        isOpen={drillModal}
        toggle={() => setDrillModal(false)}
        size="xl"
        centered
      >
        <ModalHeader
          toggle={() => setDrillModal(false)}
          style={{ borderBottom: "1px solid #f1f5f9", padding: "14px 20px" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: "16px",
              paddingRight: "24px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>
                {selectedProductDrill?.name} Customer
              </span>
              {selectedProductDrill && (
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 800,
                    color:
                      selectedProductDrill.value >= 0 ? "#15803d" : "#be123c",
                    background:
                      selectedProductDrill.value >= 0 ? "#f0fdf4" : "#fff1f2",
                    border: `1px solid ${
                      selectedProductDrill.value >= 0 ? "#bbf7d0" : "#fecdd3"
                    }`,
                    borderRadius: "8px",
                    padding: "2px 10px",
                  }}
                >
                  {fmtGBP(selectedProductDrill.value)}
                </span>
              )}
            </div>
            <SortButton
              sortOrder={modalSortStateKey}
              onToggle={handleModalSortToggle}
            />
          </div>
        </ModalHeader>
        <ModalBody
          style={{
            padding: 0,
            display: "flex",
            flexDirection: "column",
            maxHeight: "60vh",
            overflow: "hidden",
          }}
        >
          {sortedModalData.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "48px",
                color: "#94a3b8",
                fontSize: "14px",
              }}
            >
              No data available
            </div>
          ) : (
            <div style={{ overflowX: "auto", overflowY: "auto", flex: 1 }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "13px",
                }}
              >
                <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <tr
                    style={{
                      background: "#f8fafc",
                      borderBottom: "2px solid #e2e8f0",
                    }}
                  >
                    {[
                      { label: "Customer", key: "customerName", align: "left" },
                      { label: "Net Margin %", key: "netMarginPercent", align: "right" },
                      {
                        label: "Net Margin Contribution",
                        key: "netMarginContribution",
                        align: "right",
                      },
                    ].map(h => {
                      const isActive = modalSortKey === h.key
                      return (
                        <th
                          key={h.key}
                          onClick={() => handleModalHeaderSort(h.key)}
                          title={`Click to sort by ${h.label}`}
                          style={{
                            padding: "10px 16px",
                            textAlign: h.align,
                            fontSize: "12px",
                            fontWeight: 700,
                            color: isActive ? "#1d4ed8" : "#64748b",
                            background: isActive ? "#eff6ff" : "transparent",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            userSelect: "none",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              flexDirection:
                                h.align === "right" ? "row-reverse" : "row",
                            }}
                          >
                            <span>{h.label}</span>
                            <span
                              style={{
                                fontSize: "12px",
                                color: isActive ? "#2563eb" : "#94a3b8",
                                fontWeight: 800,
                              }}
                            >
                              {isActive
                                ? modalSortOrder === "asc"
                                  ? "↑"
                                  : "↓"
                                : "⇅"}
                            </span>
                          </div>
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {sortedModalData.map((row, i) => {
                    const color =
                      Number(row.netMarginContribution) >= 0
                        ? "#15803d"
                        : "#be123c"
                    return (
                      <tr
                        key={i}
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          background: i % 2 === 0 ? "#fff" : "#fafbfc",
                        }}
                      >
                        <td
                          style={{
                            padding: "10px 16px",
                            color: "#1e293b",
                            fontWeight: 600,
                          }}
                        >
                          {row.customerName}
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            textAlign: "right",
                            fontWeight: 700,
                            color,
                          }}
                        >
                          {Number(row.netMarginPercent).toFixed(1)}%
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            textAlign: "right",
                            fontWeight: 700,
                            color,
                          }}
                        >
                          {fmtGBP(row.netMarginContribution)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  )
}

export default NetMarginPage
